import * as React from "react";
import { connect } from "react-redux";
import { IGenericUserProps, genericMapStateToProps } from "../../../DataClasses/GenericUser";
import AdministrationService from "../../Services/AdministrationService";
import { firstValueFrom } from "rxjs";
import { Card, CardContent, Typography, Grid2 as Grid, Button, TextField, List, ListItem, ListItemText } from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface IHelpDocsState {
    docs: { key: string; title: string; description?: string; href: string }[];
    newDocTitle: string;
    newDocDescription: string;
    newDocFile: File | null;
    isUploading: boolean;
    uploadError: string | null;
}

export class HelpDocsPage extends React.Component<IGenericUserProps, IHelpDocsState> {
    constructor(props: any) {
        super(props);
        this.state = {
            docs: [],
            newDocTitle: "",
            newDocDescription: "",
            newDocFile: null,
            isUploading: false,
            uploadError: null,
        };
    }

    public async componentDidMount() {
        await this.loadDocs();
    }

    private async loadDocs() {
        try {
            const docs = await firstValueFrom(AdministrationService.GetHelpDocs$());
            this.setState({ docs: docs.map(d => ({ key: d.key, title: d.title, description: d.description, href: d.href })) });
        }
        catch {
            // fallback to defaults
            this.setState({ docs: [
                { key: "hedis-faq", title: "HEDIS FAQ", description: "Frequently Asked Questions", href: "/Help/2025_IDSS_FAQ.pdf" },
                { key: "idss-templates", title: "IDSS Import Templates and Materials", description: "", href: process.env.REACT_APP_TEMPLATE_AND_MATERIALS || "/Help/Templates.zip" },
                { key: "hedis-user-guide", title: "HEDIS User Guide", description: "", href: "/Help/IDSS_User_Guide.pdf" },
            ] });
        }
    }

    private addDoc = async () => {
        const { newDocTitle, newDocDescription, newDocFile } = this.state;
        if (!newDocTitle || !newDocFile) return;
        this.setState({ isUploading: true, uploadError: null });
        try {
            // 1. Upload file to S3
            const uploadResp = await firstValueFrom(AdministrationService.UploadHelpDocFile$(this.props.user, newDocFile));
            const s3Key = uploadResp.response.key;
            // 2. Add doc entry with S3 key as href
            const newDoc = { key: `${new Date().getTime()}`, title: newDocTitle, description: newDocDescription, href: s3Key };
            await firstValueFrom(AdministrationService.AddHelpDoc$(this.props.user, newDoc));
            await this.loadDocs();
            this.setState({ newDocTitle: "", newDocDescription: "", newDocFile: null, isUploading: false, uploadError: null });
        } catch (e: any) {
            this.setState({ isUploading: false, uploadError: (e?.message || "Upload failed") });
        }
    };

    private removeDoc = async (key: string) => {
        try {
            await firstValueFrom(AdministrationService.RemoveHelpDoc$(this.props.user, key));
            await this.loadDocs();
        } catch {
            // fallback: remove from client list
            this.setState({ docs: this.state.docs.filter(d => d.key !== key) });
        }
    }

    private handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            this.setState({ newDocFile: e.target.files[0] });
        } else {
            this.setState({ newDocFile: null });
        }
    };

    public render() {
        return (
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant='h6' color='primary'>Help Docs Management</Typography>
                    {this.state.uploadError && <Typography color="error">{this.state.uploadError}</Typography>}
                </Grid>
                <Grid size={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1">Add New Help Doc</Typography>
                            <Button
                              component="label"
                              role={undefined}
                              variant="contained"
                              tabIndex={-1}
                              startIcon={<CloudUploadIcon />}
                              sx={{ mt: 2, mb: 2 }}
                            >
                              {this.state.newDocFile ? this.state.newDocFile.name : "Upload file"}
                              <VisuallyHiddenInput
                                type="file"
                                accept=".pdf,.doc,.docx,.csv,.xlsx,.xls,.txt"
                                onChange={this.handleFileChange}
                              />
                            </Button>
                            <TextField fullWidth label="Title" value={this.state.newDocTitle} onChange={(e) => this.setState({ newDocTitle: e.target.value })} margin="normal" />
                            <TextField fullWidth label="Description" value={this.state.newDocDescription} onChange={(e) => this.setState({ newDocDescription: e.target.value })} margin="normal" />
                            <div style={{ marginTop: 12 }}>
                                <Button variant="contained" color="primary" onClick={this.addDoc} disabled={!this.state.newDocTitle || !this.state.newDocFile || this.state.isUploading}>
+                                    {this.state.isUploading ? "Uploading..." : "Add"}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1">Current Help Docs</Typography>
                            <List>
                                {this.state.docs.map(d => (
                                    <ListItem key={d.key} divider secondaryAction={<Button onClick={() => this.removeDoc(d.key)}>Remove</Button>}>
                                        <ListItemText primary={<a href={d.href} target="_blank" rel="noreferrer">{d.title}</a>} secondary={d.description} />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    }
}

const HelpDocsPageWithState = connect<IGenericUserProps>(genericMapStateToProps)(HelpDocsPage as any);

export default HelpDocsPageWithState;
